import React from "react";
import styled from "styled-components";
import { shorten } from "@backpacker/number";
import {
  FontFamily,
  FontWeight,
  content,
  ellipsis,
  form,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import Icon from "@ui-kit/icon";
import Portrait from "@ui-kit/identity/portrait";
import Tooltip from "@ui-kit/tooltip";
import { tr } from "@radarly/i18n";
import {
  InfluencerLensFactory,
  SearchableInfluencer,
} from "@radarly/models/influencer";

type ScreenSize = "small" | "medium" | "large";
type Influencer = SearchableInfluencer & { isPhantom?: boolean };
export interface IProps {
  index: number;
  locale: Locale;
  influencer: Influencer;
  mainUnit?: Metric;
  screenSize: ScreenSize;
  isTooltipDisabled?: boolean;
  tooltip: (influencer: SearchableInfluencer) => React.ReactNode;
  className?: string;
}

const RootPhantom = styled.div`
  height: 83px;
`;

const Root = styled.div`
  padding: 0 12px;
  font-size: 14px;
  color: ${content};
  height: 83px;
  display: flex;
  align-items: center;
  font-family: ${FontFamily.Sans};
  font-weight: ${FontWeight.Normal};
  &:hover {
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
`;

const Index = styled.div`
  font-weight: ${FontWeight.Normal};
  width: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const StyledIcon = styled(Icon)`
  margin-right: 6px;
`;

const StyledPortrait = styled(Portrait)<{ screenSize: ScreenSize }>`
  display: block;
  margin-right: 6px;
`;

const SocialName = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.div`
  font-weight: ${FontWeight.Normal};
  ${ellipsis("180px")};
`;

const StyledSmallInfo = styled.div`
  font-size: 12px;
  display: flex;
  align-items: center;
`;

const SmallInfoMain = styled.div`
  margin-right: 6px;
  font-weight: ${FontWeight.Normal};
`;

const Infos = styled.div<{ screenSize: ScreenSize }>`
  flex: ${({ screenSize }) =>
    screenSize === "small"
      ? "0 0 100%"
      : screenSize === "medium"
      ? "0 0 65%"
      : "0 0 50%"};
  display: flex;
  align-items: center;
`;

const Figures = styled.div<{ screenSize?: ScreenSize }>`
  display: ${({ screenSize }) => (screenSize === "small" ? "none" : "flex")};
  font-weight: ${FontWeight.Normal};
  align-items: center;
  justify-content: ${({ screenSize }) =>
    screenSize === "medium" ? "center" : "space-between"};
  width: 100%;
  color: ${(props) => content(props, "secondary")};
`;

const FigureWrapper = styled.div<{
  isMainUnit: boolean;
  screenSize?: ScreenSize;
}>`
  color: ${(props) => props.isMainUnit && content(props, "primary")};
  width: ${({ screenSize }) => (screenSize === "medium" ? "100%" : "33%")};
  text-align: ${({ screenSize }) =>
    screenSize === "medium" ? "center" : "left"};
`;

const Number = styled.div<{ isCentered?: boolean }>`
  text-align: ${({ isCentered }) => (isCentered ? "center" : "right")};
  width: ${({ isCentered }) => (isCentered ? "inherit" : "max-content")};
`;

const StyledPostsCount = styled.div`
  font-size: 12px;
  font-weight: ${FontWeight.Normal};
  color: ${(props) => content(props, "secondary")};
`;

const PostsCount = ({ locale, count }: { locale: Locale; count: number }) => {
  const getPostsCount = (locale: Locale, count: number) =>
    tr(locale, "INFLUENCERS::TABLE::COLUMN::POSTSCOUNT", {
      countString: shorten(locale, count),
      count,
    });
  return <StyledPostsCount>({getPostsCount(locale, count)})</StyledPostsCount>;
};

const SmallInfo = ({
  locale,
  influencer,
  mainUnit,
}: {
  locale: Locale;
  influencer: Influencer;
  mainUnit: Metric;
}) => {
  return (
    <StyledSmallInfo>
      <SmallInfoMain>
        {shorten(locale, influencer.counts[mainUnit])}
      </SmallInfoMain>
      {mainUnit !== Metric.Doc && (
        <PostsCount locale={locale} count={influencer.counts[Metric.Doc]} />
      )}
    </StyledSmallInfo>
  );
};

const RadarlyWidgetTopAuthorsInfoline = ({
  index,
  locale,
  influencer,
  mainUnit,
  screenSize,
  isTooltipDisabled,
  tooltip,
  className,
}: IProps) => {
  const influencerLens = InfluencerLensFactory.create(influencer.user);

  return influencer.isPhantom ? (
    <RootPhantom />
  ) : (
    <Tooltip disabled={isTooltipDisabled} tooltip={tooltip(influencer)}>
      <Root className={className}>
        <Infos screenSize={screenSize}>
          <Index>{index}</Index>
          <StyledIcon size={16} glyph={influencer.user.platform} />
          <StyledPortrait
            size={32}
            picture={influencerLens.getAvatar()}
            screenSize={screenSize}
          />
          <SocialName>
            <Name>{influencerLens.getName()}</Name>
            {screenSize === "small" && (
              <SmallInfo
                locale={locale}
                influencer={influencer}
                mainUnit={mainUnit}
              />
            )}
          </SocialName>
        </Infos>
        <Figures screenSize={screenSize}>
          {screenSize === "medium" && (
            <FigureWrapper isMainUnit screenSize={screenSize}>
              <Number isCentered>
                {shorten(locale, influencer.counts[mainUnit])}
              </Number>
              {mainUnit !== Metric.Doc && (
                <PostsCount
                  locale={locale}
                  count={influencer.counts[Metric.Doc]}
                />
              )}
            </FigureWrapper>
          )}
          {screenSize === "large" && (
            <>
              <FigureWrapper isMainUnit={mainUnit === Metric.Doc}>
                <Number>
                  {shorten(locale, influencer.counts[Metric.Doc])}
                </Number>
              </FigureWrapper>
              <FigureWrapper isMainUnit={mainUnit === Metric.Reach}>
                <Number>
                  {shorten(locale, influencer.counts[Metric.Reach])}
                </Number>
              </FigureWrapper>
              <FigureWrapper isMainUnit={mainUnit === Metric.Engagement}>
                <Number>
                  {shorten(locale, influencer.counts[Metric.Engagement])}
                </Number>
              </FigureWrapper>
            </>
          )}
        </Figures>
      </Root>
    </Tooltip>
  );
};

export default RadarlyWidgetTopAuthorsInfoline;
